import { isEventMuted } from "features/CenterContent/RoleContent/EventHandling/components/InfraEventCard/useEventCardState";
import {
  isEventSeen,
  isRecentlyChanged,
} from "features/CenterContent/RoleContent/EventHandling/utils";
import { useOptionalEventHandlingContext } from "features/CenterContent/RoleContent/EventHandling/EventHandlingContext";
import { useMemo } from "react";
import { PlanMeasureKey } from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/types";
import { AlternativeTransportPlansResponse } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPlans";
import { Nullish } from "shared/utils/objectUtils";

export type EventHandlingCounts = {
  notifying: number;
  changed: number;
  total: number;
};

function computeEventPlanCounts(
  plans: Nullish<AlternativeTransportPlansResponse>,
) {
  let notifying = 0;
  let changed = 0;
  let both = 0;

  if (plans) {
    Object.values(plans).forEach((measure) => {
      Object.entries(measure).forEach(([key, informationArray]) => {
        informationArray.forEach(({ information }) => {
          const isChanged =
            information.state !== "CLOSED" &&
            information.version > 1 &&
            !isEventSeen(information.uuid, information.version) &&
            isRecentlyChanged(information);

          if (isChanged) changed += 1;

          if ((key as PlanMeasureKey) !== "noCoverage") return;

          const isNotifying =
            information.state !== "CLOSED" && !isEventMuted(information.uuid);

          if (isNotifying) notifying += 1;
          if (isChanged && isNotifying) both += 1;
        });
      });
    });
  }

  return { notifying, changed, total: notifying + changed - both };
}

export const useEventHandlingCounts = (
  plans: Nullish<AlternativeTransportPlansResponse>,
): EventHandlingCounts => {
  const ctx = useOptionalEventHandlingContext();
  const muteChangeCounter = ctx?.muteChangeCounter ?? 0;
  const seenChangeCounter = ctx?.seenChangeCounter ?? 0;

  const counts = useMemo(
    () => computeEventPlanCounts(plans),
    [plans, muteChangeCounter, seenChangeCounter],
  );

  return counts;
};
